/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import { getCookie } from "../utils/cookieUtils";
import { getGatewayIdToken } from "./gatewaySession";

const documentsApiBaseUrl = import.meta.env.VITE_DOCUMENTS_API;

const documentsApi = axios.create({
  baseURL: documentsApiBaseUrl,
});

// Add an interceptor to include the Authorization header, if available.
documentsApi.interceptors.request.use(async (config) => {
  const token = getCookie("authToken");
  if (token) config.headers.Authorization = `Bearer ${await getGatewayIdToken()}`;
  return config;
});

// --- DTOs ---

export interface DocumentResponse {
  id: string;
  name: string;
  s3_key: string;
  verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocumentCreateResponse {
  message: string;
  id: string;
  upload_url: string;
  upload_fields: Record<string, string>;
}

export interface DocumentDownloadResponse {
  message: string;
  download_url: string;
}

export interface VerifyResponse {
  message: string;
}

export interface ListDocumentsResponse {
  documents: DocumentResponse[];
  next_page_token?: string;
}

export interface DocumentCreateRequest {
  name: string;
}

// --- API Methods ---

/**
 * Creates a new document. The request should include the document name.
 * Returns the created document's ID and an upload URL for S3.
 */
export const createDocument = async (
  document: DocumentCreateRequest
): Promise<DocumentCreateResponse> => {
  const response = await documentsApi.post<DocumentCreateResponse>(
    "/api/document",
    document
  );
  return response.data;
};

export const uploadDocument = async (
  presignedURL: string,
  fields: Record<string, string>,
  file: File
): Promise<void> => {
  try {
    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) form.append(key, value);
    form.append("file", file);
    await axios.post(presignedURL, form);
  } catch (error) {
    console.error("Error uploading media", error);
    throw new Error("Failed to upload media");
  }
};

/**
 * Retrieves a document's metadata by its ID.
 */
export const getDocument = async (id: string): Promise<DocumentResponse> => {
  const response = await documentsApi.get<DocumentResponse>("/api/document", {
    params: { id },
  });
  return response.data;
};

/**
 * Lists documents with optional filtering by name and pagination.
 */
export const listDocuments = async (
  limit = 10,
  startAfter?: string,
  name?: string
): Promise<ListDocumentsResponse> => {
  const params: any = { limit };
  if (startAfter) params.start_after = startAfter;
  if (name) params.name = name;

  const response = await documentsApi.get<ListDocumentsResponse>(
    "/api/documents",
    { params }
  );
  return response.data;
};

/**
 * Deletes a document by its ID.
 */
export const deleteDocument = async (
  id: string
): Promise<{ message: string }> => {
  const response = await documentsApi.delete<{ message: string }>(
    "/api/document",
    {
      params: { id },
    }
  );
  return response.data;
};

/**
 * Verifies that the document exists in S3 and updates its verification status.
 */
export const verifyDocument = async (id: string): Promise<VerifyResponse> => {
  const response = await documentsApi.post<VerifyResponse>(
    "/api/document/verify", undefined, { params: { id } }
  );
  return response.data;
};

/**
 * Generates a presigned URL for downloading the document.
 */
export const downloadDocument = async (
  id: string
): Promise<DocumentDownloadResponse> => {
  const response = await documentsApi.get<DocumentDownloadResponse>(
    "/api/document/download",
    { params: { id } }
  );
  return response.data;
};
